import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type ScheduleDatePickerProps = {
  value: string;
  onChange: (value: string) => void;
};

const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export function ScheduleDatePicker({
  value,
  onChange,
}: ScheduleDatePickerProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => monthFor(value));
  const [draft, setDraft] = useState(value);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  const closeAndFocus = () => {
    setOpen(false);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  };
  const commit = () => {
    if (!draft) return;
    onChange(draft);
    closeAndFocus();
  };
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const leading = new Date(year, monthIndex, 1).getDay();
  const count = new Date(year, monthIndex + 1, 0).getDate();

  return (
    <div ref={rootRef} className="schedule-date-picker">
      <button
        ref={triggerRef}
        type="button"
        className="schedule-toolbar-field schedule-date-field"
        aria-label="Choose date"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          if (open) {
            setOpen(false);
            return;
          }
          setDraft(value);
          setMonth(monthFor(value));
          setOpen(true);
          window.setTimeout(() => selectedRef.current?.focus(), 0);
        }}
      >
        <span className="schedule-toolbar-value">
          <span className={value ? undefined : "is-placeholder"}>
            {value ? displayDate(value) : "Select date"}
          </span>
          <CalendarDays size={16} aria-hidden="true" />
        </span>
      </button>
      {open ? (
        <div className="schedule-date-popover" role="dialog" aria-label="Choose date">
          <div className="schedule-date-popover-header">
            <button
              type="button"
              className="icon-button bordered"
              aria-label="Previous month"
              onClick={() => setMonth(new Date(year, monthIndex - 1, 1))}
            >
              <ChevronLeft size={16} aria-hidden="true" />
            </button>
            <strong>
              {months[monthIndex]} {year}
            </strong>
            <button
              type="button"
              className="icon-button bordered"
              aria-label="Next month"
              onClick={() => setMonth(new Date(year, monthIndex + 1, 1))}
            >
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
          <div className="schedule-date-weekdays" aria-hidden="true">
            {weekdays.map((weekday) => (
              <span key={weekday}>{weekday}</span>
            ))}
          </div>
          <div className="schedule-date-days">
            {Array.from({ length: leading }, (_, index) => (
              <span key={`empty-${index}`} aria-hidden="true" />
            ))}
            {Array.from({ length: count }, (_, index) => {
              const day = index + 1;
              const date = new Date(year, monthIndex, day);
              const iso = isoDate(date);
              const selected = iso === draft;
              return (
                <button
                  ref={selected ? selectedRef : undefined}
                  key={iso}
                  type="button"
                  aria-label={date.toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                  aria-pressed={selected}
                  className={selected ? "is-selected" : undefined}
                  onClick={() => setDraft(iso)}
                >
                  {day}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="primary-button schedule-date-confirm"
            disabled={!draft}
            onPointerDown={(event) => {
              event.preventDefault();
              commit();
            }}
            onClick={commit}
          >
            Set date
          </button>
        </div>
      ) : null}
    </div>
  );
}

function parseDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match?.[1] || !match[2] || !match[3]) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function monthFor(value: string): Date {
  const date = parseDate(value) ?? new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function isoDate(date: Date): string {
  return `${date.getFullYear().toString().padStart(4, "0")}-${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
}

function displayDate(value: string): string {
  const date = parseDate(value);
  if (!date) return value;
  return `${months[date.getMonth()]?.slice(0, 3)} ${date.getDate()}, ${date.getFullYear()}`;
}
